import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { customersApi, type CustomerWithStats, type CustomerDetail } from '../lib/api-hooks';
import { ticketsApi } from '../lib/api-hooks';
import { clsx } from 'clsx';

const STATUS_LABELS: Record<string, string> = {
  NY: 'Ny', UNDER_ARBEID: 'Under arbeid', VENTER_KUNDE: 'Venter kunde',
  VENTER_INTERNT: 'Venter internt', LOST: 'Løst', LUKKET: 'Lukket',
};
const STATUS_COLORS: Record<string, string> = {
  NY: 'bg-blue-100 text-blue-700', UNDER_ARBEID: 'bg-amber-100 text-amber-700',
  VENTER_KUNDE: 'bg-purple-100 text-purple-700', VENTER_INTERNT: 'bg-orange-100 text-orange-700',
  LOST: 'bg-green-100 text-green-700', LUKKET: 'bg-slate-100 text-slate-600',
};
const PRIORITET_LABELS: Record<string, string> = { LAV: 'Lav', NORMAL: 'Normal', HOY: 'Høy', KRITISK: 'Kritisk' };

function formatSakNr(n: number): string { return `SAK-${String(n).padStart(4, '0')}`; }
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}
function getInitials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';
}

export default function CustomersPage() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => customersApi.list(search || undefined),
    refetchInterval: 60_000,
  });

  return (
    <div className="mx-auto flex h-[calc(100vh-49px)] max-w-7xl">
      <div className="flex w-full flex-col border-r border-slate-200 md:w-96">
        <div className="border-b border-slate-200 bg-white px-4 py-3">
          <div className="mb-3 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-800">Kunder</h1>
            <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
              Ny kunde
            </button>
          </div>
          <div className="relative">
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk etter navn, e-post eller firma…" className="input pl-9" autoFocus />
            <svg className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-slate-400"><span className="animate-pulse">Laster kunder…</span></div>
          ) : customers?.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <svg className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              <p className="text-sm">{search ? 'Ingen treff.' : 'Ingen kunder enda.'}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {customers?.map((c) => (
                <CustomerRow key={c.id} customer={c} isSelected={selectedId === c.id} onClick={() => setSelectedId(c.id)} />
              ))}
            </div>
          )}
        </div>
        {customers && (
          <div className="border-t border-slate-200 bg-white px-4 py-2 text-xs text-slate-400">
            {customers.length} {customers.length === 1 ? 'kunde' : 'kunder'}
          </div>
        )}
      </div>
      {selectedId && (
        <div className="hidden flex-1 overflow-y-auto md:block">
          <CustomerDetailPanel customerId={selectedId} />
        </div>
      )}
      {selectedId && (
        <div className="fixed inset-0 z-50 bg-white md:hidden" onClick={() => setSelectedId(null)}>
          <div className="h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedId(null)} className="sticky top-0 z-10 flex w-full items-center gap-2 border-b border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>
              Tilbake
            </button>
            <CustomerDetailPanel customerId={selectedId} />
          </div>
        </div>
      )}
      {showCreate && <CreateCustomerModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CustomerRow({ customer, isSelected, onClick }: { customer: CustomerWithStats; isSelected: boolean; onClick: () => void }) {
  const total = customer._count.tickets;
  const open = (customer.byStatus['NY'] || 0) + (customer.byStatus['UNDER_ARBEID'] || 0) + (customer.byStatus['VENTER_KUNDE'] || 0) + (customer.byStatus['VENTER_INTERNT'] || 0);
  const resolved = (customer.byStatus['LOST'] || 0) + (customer.byStatus['LUKKET'] || 0);
  return (
    <button onClick={onClick} className={clsx('flex w-full flex-col gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50', isSelected && 'bg-brand-50 hover:bg-brand-50')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800">{customer.navn || customer.firma || customer.epost}</p>
          <p className="truncate text-xs text-slate-400">{customer.epost}</p>
          {customer.firma && customer.navn && <p className="truncate text-xs text-slate-400">{customer.firma}</p>}
        </div>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">{getInitials(customer.navn || customer.firma || customer.epost)}</div>
      </div>
      {total > 0 && (
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-slate-600">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            {total} totalt
          </span>
          {open > 0 && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">{open} åpne</span>}
          {resolved > 0 && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{resolved} løst</span>}
        </div>
      )}
    </button>
  );
}

function CustomerDetailPanel({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showNewTicket, setShowNewTicket] = useState(false);
  const { data: customer, isLoading } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customersApi.get(customerId),
  });
  const createTicketMutation = useMutation({
    mutationFn: (tittel: string) => ticketsApi.create({ tittel, customerId }),
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', customerId] });
      navigate(`/ticket/${ticket.id}`);
    },
  });
  if (isLoading || !customer) return <div className="flex h-full items-center justify-center text-slate-400"><span className="animate-pulse">Laster…</span></div>;
  const total = customer._count.tickets;
  const statuses: string[] = ['NY', 'UNDER_ARBEID', 'VENTER_KUNDE', 'VENTER_INTERNT', 'LOST', 'LUKKET'];
  return (
    <div className="px-6 py-6">
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-brand text-lg font-semibold text-white">{getInitials(customer.navn || customer.firma || customer.epost)}</div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">{customer.navn || customer.firma || customer.epost}</h2>
            <p className="text-sm text-slate-500">{customer.epost}</p>
            {customer.telefon && <p className="text-sm text-slate-500">{customer.telefon}</p>}
            {customer.firma && customer.navn && <p className="text-sm text-slate-500">{customer.firma}</p>}
            <p className="mt-1 text-xs text-slate-400">Kunde siden {customer.opprettet ? formatDate(customer.opprettet) : 'ukjent'}</p>
          </div>
        </div>
        <button onClick={() => setShowNewTicket(true)} className="btn-primary text-sm">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
          Opprett sak
        </button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {statuses.map((status) => {
          const count = customer.byStatus[status] || 0;
          return (
            <div key={status} className="card p-3 text-center">
              <div className={clsx('mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold', STATUS_COLORS[status])}>{count}</div>
              <p className="text-xs text-slate-500">{STATUS_LABELS[status]}</p>
            </div>
          );
        })}
      </div>
      <div className="mb-4 flex items-center gap-2">
        <h3 className="font-medium text-slate-800">Saker</h3>
        <span className="chip bg-slate-100 text-slate-600">{total}</span>
      </div>
      {customer.tickets.length === 0 ? (
        <div className="card p-8 text-center text-slate-400"><p className="text-sm">Ingen saker for denne kunden enda.</p></div>
      ) : (
        <div className="space-y-2">
          {customer.tickets.map((t) => (
            <button key={t.id} onClick={() => navigate(`/ticket/${t.id}`)} className="card flex w-full items-center justify-between p-4 text-left transition-colors hover:border-brand-300">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400">{formatSakNr(t.sakNr)}</span>
                  <span className={clsx('chip text-xs', STATUS_COLORS[t.status])}>{STATUS_LABELS[t.status]}</span>
                  <span className="text-xs text-slate-400">{PRIORITET_LABELS[t.prioritet]}</span>
                </div>
                <p className="mt-1 truncate text-sm font-medium text-slate-800">{t.tittel}</p>
                <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                  <span>{formatDate(t.opprettet)}</span>
                  {t.kategori && <span>{t.kategori}</span>}
                  {t.assignee && <span>Tildelt: {t.assignee.navn}</span>}
                </div>
              </div>
              <svg className="h-4 w-4 shrink-0 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          ))}
        </div>
      )}
      {showNewTicket && (
        <NewTicketForCustomerModal customer={customer} onClose={() => setShowNewTicket(false)} onCreate={(tittel) => createTicketMutation.mutate(tittel)} creating={createTicketMutation.isPending} />
      )}
    </div>
  );
}

function CreateCustomerModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [epost, setEpost] = useState('');
  const [navn, setNavn] = useState('');
  const [telefon, setTelefon] = useState('');
  const [firma, setFirma] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useMutation({
    mutationFn: () => customersApi.create({ epost, navn: navn || undefined, telefon: telefon || undefined, firma: firma || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); onClose(); },
    onError: (err: any) => { setError(err?.message ?? 'Kunne ikke opprette kunde.'); },
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Ny kunde</h2>
          <button onClick={onClose} className="btn-ghost p-1"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (epost.trim()) createMutation.mutate(); }} className="space-y-3">
          <div><label className="mb-1 block text-sm font-medium text-slate-700">E-post *</label><input type="email" required value={epost} onChange={(e) => setEpost(e.target.value)} placeholder="kunde@eksempel.no" className="input" autoFocus /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Navn</label><input type="text" value={navn} onChange={(e) => setNavn(e.target.value)} placeholder="Kari Nordmann" className="input" /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Telefon</label><input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="123 45 678" className="input" /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Firma</label><input type="text" value={firma} onChange={(e) => setFirma(e.target.value)} placeholder="Bedrift AS" className="input" /></div>
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Avbryt</button>
            <button type="submit" disabled={createMutation.isPending || !epost.trim()} className="btn-primary">{createMutation.isPending ? 'Oppretter…' : 'Opprett kunde'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewTicketForCustomerModal({ customer, onClose, onCreate, creating }: { customer: CustomerDetail; onClose: () => void; onCreate: (tittel: string) => void; creating: boolean }) {
  const [tittel, setTittel] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div><h2 className="text-lg font-semibold text-slate-800">Ny sak</h2><p className="text-sm text-slate-500">For {customer.navn || customer.epost}</p></div>
          <button onClick={onClose} className="btn-ghost p-1"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (tittel.trim()) onCreate(tittel.trim()); }} className="space-y-3">
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Tittel *</label><input type="text" required value={tittel} onChange={(e) => setTittel(e.target.value)} placeholder="Kort beskrivelse av saken" className="input" autoFocus /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Avbryt</button>
            <button type="submit" disabled={creating || !tittel.trim()} className="btn-primary">{creating ? 'Oppretter…' : 'Opprett sak'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
